import { Link } from "@tanstack/react-router";

import { CtaBand, PageHero } from "@/components/site/Page";
import { InlineCta } from "@/components/site/InlineCta";
import type { ComparisonContent } from "@/content/school-comparison";
import type { Locale } from "@/lib/i18n";

/**
 * Delad layout för jämförelseguiden svensk/internationell/spansk skola.
 * Allt innehåll kommer från src/content/school-comparison.ts.
 */

/** Färgkod per skolform så raderna går att skilja åt direkt. */
const schoolAccents: { match: RegExp; dot: string; ring: string }[] = [
  { match: /^svensk/i, dot: "bg-primary", ring: "border-l-primary" },
  { match: /^internationell|^international/i, dot: "bg-sky-600", ring: "border-l-sky-600" },
  { match: /^spansk|^spanish/i, dot: "bg-amber-600", ring: "border-l-amber-600" },
];

function accentFor(label: string) {
  return schoolAccents.find((a) => a.match.test(label));
}
export function SchoolComparisonPage({
  locale,
  content,
}: {
  locale: Locale;
  content: ComparisonContent;
}) {
  const c = content.columnLabels;

  return (
    <>
      <PageHero eyebrow={content.eyebrow} title={content.title} intro={content.intro} />

      <div className="mx-auto max-w-3xl px-5 pt-10">
        <p className="rounded-xl border border-border bg-sand px-5 py-4 text-sm leading-relaxed text-foreground/80">
          {content.neutralityNote}
        </p>
      </div>

      <section className="mx-auto max-w-5xl px-5 pt-10">
        <h2 className="text-2xl font-semibold">{content.tableHeading}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{content.tableIntro}</p>

        {/* Tabell på större skärmar */}
        <div className="mt-6 hidden overflow-hidden rounded-xl border border-border md:block">
          <table className="w-full text-left text-sm">
            <thead className="bg-sand text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {c.row}
                </th>
                {[c.swedish, c.international, c.spanish].map((label) => {
                  const accent = accentFor(label);
                  return (
                    <th key={label} scope="col" className="px-4 py-3 font-semibold">
                      <span className="flex items-center gap-2">
                        {accent ? (
                          <span
                            aria-hidden
                            className={`h-2.5 w-2.5 shrink-0 rounded-full ${accent.dot}`}
                          />
                        ) : null}
                        {label}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {content.rows.map((row) => (
                <tr key={row.label} className="border-t border-border align-top">
                  <th scope="row" className="px-4 py-3 text-left font-medium text-foreground">
                    {row.label}
                  </th>
                  <td className="px-4 py-3 text-foreground/80">{row.swedish}</td>
                  <td className="px-4 py-3 text-foreground/80">{row.international}</td>
                  <td className="px-4 py-3 text-foreground/80">{row.spanish}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Kortvy på mobil */}
        <div className="mt-6 space-y-4 md:hidden">
          {content.rows.map((row) => {
            const cells: [string, string][] = [
              [c.swedish, row.swedish],
              [c.international, row.international],
              [c.spanish, row.spanish],
            ];
            return (
              <div key={row.label} className="rounded-xl border border-border bg-card p-4">
                <p className="text-sm font-semibold text-foreground">{row.label}</p>
                <dl className="mt-3 space-y-3 text-sm">
                  {cells.map(([label, body]) => {
                    const accent = accentFor(label);
                    return (
                      <div key={label} className={accent ? `border-l-4 pl-3 ${accent.ring}` : ""}>
                        <dt className="flex items-center gap-2 font-semibold text-foreground">
                          {accent ? (
                            <span
                              aria-hidden
                              className={`h-2.5 w-2.5 shrink-0 rounded-full ${accent.dot}`}
                            />
                          ) : null}
                          {label}
                        </dt>
                        <dd className="mt-0.5 text-foreground/85">{body}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{content.tableFootnote}</p>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <div className="space-y-14">
          {content.sections.map((section, index) => (
            <article key={section.heading} className="border-t border-border pt-6">
              <div className="flex items-baseline gap-4">
                <span
                  aria-hidden
                  className="font-display text-sm font-semibold tabular-nums tracking-[0.18em] text-primary/70"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h2 className="font-display text-2xl leading-tight font-semibold text-foreground sm:text-3xl">
                  {section.heading}
                </h2>
              </div>

              <div className="mt-5 grid gap-x-10 gap-y-4 sm:grid-cols-2">
                {section.paragraphs.map((text) => (
                  <p key={text} className="text-base leading-relaxed text-foreground/85">
                    {text}
                  </p>
                ))}
              </div>

              {section.bullets?.length ? (
                <dl className="mt-8 space-y-3">
                  {section.bullets.map((item) => {
                    const split = item.indexOf(":");
                    const label = split > 0 ? item.slice(0, split) : "";
                    const body = split > 0 ? item.slice(split + 1).trim() : item;
                    const accent = accentFor(label);
                    return (
                      <div
                        key={item}
                        className={`rounded-lg border border-border/70 bg-card px-4 py-3 ${
                          accent ? `border-l-4 ${accent.ring}` : ""
                        }`}
                      >
                        {label ? (
                          <dt className="flex items-center gap-2 text-sm font-semibold text-foreground">
                            {accent ? (
                              <span
                                aria-hidden
                                className={`h-2.5 w-2.5 shrink-0 rounded-full ${accent.dot}`}
                              />
                            ) : null}
                            {label}
                          </dt>
                        ) : (
                          <dt className="sr-only">–</dt>
                        )}
                        <dd className="mt-1 text-sm leading-relaxed text-foreground/85 sm:text-base">
                          {body}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-4">
        <h2 className="text-2xl font-semibold">{content.checklistHeading}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{content.checklistIntro}</p>
        <ul className="mt-6 space-y-3">
          {content.checklist.map((item) => (
            <li
              key={item}
              className="rounded-xl border border-border bg-card px-4 py-3 text-sm leading-relaxed text-foreground/85"
            >
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-12">
        <h2 className="text-2xl font-semibold">{content.ownPerspectiveHeading}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {content.ownPerspectiveNote}
        </p>
        <ul className="mt-6 list-disc space-y-2 pl-5 text-base leading-relaxed text-foreground/90">
          {content.ownPerspective.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="mt-8">
          <InlineCta locale={locale} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-12">
        <h2 className="text-2xl font-semibold">{content.faqHeading}</h2>
        <dl className="mt-6 space-y-5">
          {content.faq.map((item) => (
            <div key={item.q} className="rounded-xl border border-border bg-card p-5">
              <dt className="text-base font-semibold text-foreground">{item.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-foreground/85">{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-12">
        <ul className="flex flex-wrap gap-3">
          {content.links.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to as never}
                className="link-chip inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium text-primary"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <CtaBand locale={locale} title={content.ctaTitle} text={content.ctaText} />
      <div className="h-20" />
    </>
  );
}
